/**
 * Русские естественные предложения для переноса навыка.
 *
 * Предложения короткие и осмысленные: машинные конструкции вида
 * «фыв авп олд» не тренируют реальный набор (ТЗ §4.6, §8.3).
 *
 * `focusWords` — начальные формы: словарь помечает предложение леммой, а в
 * тексте слово стоит в своей форме. Набор покрывает начало частотного списка,
 * чтобы у частого слабого слова нашлось предложение.
 */

import type { SentenceEntry } from './types.js';

/** Собирает запись предложения: язык подставляется один раз на весь список. */
function ru(text: string, focusWords: readonly string[]): SentenceEntry {
  return { text, language: 'ru', focusWords };
}

export const RU_SENTENCES: readonly SentenceEntry[] = [
  ru('Я не знаю, что он хотел сказать.', ['я', 'не', 'что', 'он', 'хотеть']),
  ru('Мы живём в этом городе уже седьмой год.', ['мы', 'в', 'этот', 'уже', 'жить', 'город', 'год']),
  ru('Это дело можно закончить за один день.', ['это', 'дело', 'мочь', 'за', 'один', 'день']),
  ru('Он взял книгу и открыл первую страницу.', ['он', 'взять', 'книга', 'и', 'открыть', 'первый']),
  ru('Она сказала, что придёт завтра утром.', ['она', 'что', 'говорить', 'завтра', 'утро']),
  ru('Они работают вместе почти каждый день.', ['они', 'работать', 'вместе', 'день']),
  ru('Как вы решили этот вопрос?', ['как', 'вы', 'решить', 'этот', 'вопрос']),
  ru('Из всех книг он выбрал самую тонкую.', ['из', 'весь', 'книга', 'он', 'самый']),
  ru('У меня нет времени на длинные споры.', ['у', 'нет', 'время', 'на', 'долгий']),
  ru('Человек, который сидит рядом, читает книгу.', [
    'человек',
    'который',
    'рядом',
    'читать',
    'книга',
  ]),
  ru('Мы не могли ждать до вечера.', ['мы', 'не', 'мочь', 'ждать', 'вечер']),
  ru('Так бывает только тогда, когда слишком спешишь.', ['так', 'только', 'тогда', 'когда']),
  ru('Для тебя это не составит труда.', ['для', 'это', 'не']),
  ru('Она думала о своём ответе весь вечер.', ['она', 'о', 'своё', 'ответить', 'весь', 'думать']),
  ru('Он же сам просил помочь с работой.', ['он', 'же', 'сам', 'с', 'работа']),
  ru('Ваш ответ оказался почти точным.', ['ваш', 'ответить', 'точность']),
  ru('Наш дом стоит под большим деревом.', ['наш', 'дом', 'стоять', 'под', 'большой']),
  ru('Даже маленькая ошибка стоит времени.', ['даже', 'ошибка', 'стоять', 'время']),
  ru('Другой такой возможности уже не будет.', ['другой', 'такой', 'уже', 'быть']),
  ru('Если ты устал, сделай небольшой перерыв.', ['если', 'сделать']),
  ru('Она держит руки на домашнем ряду.', ['она', 'рука', 'на']),
  ru('Ни один палец не уходит со своего места.', ['ни', 'один', 'не', 'со', 'место']),
  ru('Пальцы сами находят нужные клавиши.', ['сам', 'найти', 'нужно', 'ключ']),
  ru('К вечеру он стал печатать заметно быстрее.', ['к', 'вечер', 'стать', 'быстрый', 'писать']),
  ru('По утрам буквы даются легче.', ['по', 'утро', 'буква', 'лёгкий']),
  ru('А что, если начать сначала?', ['а', 'что', 'если', 'начать', 'сначала']),
  ru('Но времени на это уже нет.', ['но', 'время', 'на', 'уже', 'нет']),
  ru('То, что было трудно вчера, сегодня выходит само.', [
    'то',
    'что',
    'быть',
    'вчера',
    'сегодня',
    'сам',
  ]),
  ru('Себя обмануть проще всего.', ['себя', 'весь', 'простой']),
  ru('Какой язык вы выбрали для работы?', ['какой', 'вы', 'для', 'работа']),
  ru('Свой текст он проверяет дважды.', ['свой', 'он']),
  ru('От этого зависит скорость всей строки.', ['от', 'скорость', 'весь', 'строка']),
  ru('Ещё немного, и рука запомнит движение.', ['ещё', 'и', 'рука', 'помнить']),
  ru('Мы бы справились быстрее вдвоём.', ['мы', 'бы', 'быстрый']),
  ru('Свои ошибки полезно разбирать вслух.', ['свои', 'ошибка']),
  ru('Он один отвечает за общий результат.', ['он', 'один', 'ответить', 'за', 'общий']),
  ru('Внимание важнее, чем скорость руки.', ['внимание', 'важный', 'скорость', 'рука']),
  ru('Скорость растёт, когда точность уже высокая.', ['скорость', 'когда', 'точность', 'уже']),
  ru('Сегодня я сделал меньше ошибок, чем вчера.', ['сегодня', 'я', 'сделать', 'ошибка', 'вчера']),
  ru('Тёмный экран и светлые буквы.', ['тёмный', 'экран', 'светлый', 'буква']),
  ru('Хорошая привычка формируется медленно.', ['хороший', 'привычка']),
  ru('Ёж пробежал по тёплой земле.', ['по', 'тёплый', 'земля']),
  ru('Новый день начинается с обычных дел.', ['новый', 'день', 'начать', 'с', 'дело']),
  ru('Он читает по одной странице в час.', ['он', 'читать', 'по', 'один', 'в', 'час']),
  ru('Мы жили в этом городе три года.', ['мы', 'жить', 'в', 'этот', 'город', 'год']),
  ru('Память держит слово, если рука его повторила.', ['память', 'слово', 'если', 'рука']),
  ru('Здесь нужно нажать пробел и продолжить строку.', ['здесь', 'нужно', 'и', 'строка']),
  ru('Между двумя ошибками прошла целая минута.', ['между', 'ошибка', 'минута']),
  ru('Без внимания скорость не растёт.', ['без', 'внимание', 'скорость', 'не']),
  ru('Теперь он видит всю строку целиком.', ['теперь', 'он', 'видеть', 'весь', 'строка']),

  // Предложения со знаками препинания: режим «Пунктуация» тренирует знаки
  // внутри текста, а не в отрыве от него (ТЗ §6, режим 9).
  ru('Он сказал: "Приходи завтра", - и ушёл.', ['он', 'говорить']),
  ru('Кто-то уже начал; другие ещё думают.', ['уже', 'начать', 'другой', 'ещё', 'думать']),
  ru('Из-за дождя мы остались дома.', ['из', 'мы', 'дом']),
  ru('Как же это просто!', ['как', 'же', 'это', 'простой']),
  ru('Нужно всего одно: не спешить.', ['нужно', 'один', 'не']),
  ru('Он спросил, куда мы идём (и не получил ответа).', ['он', 'спросить', 'мы', 'идти', 'не']),
  ru('Что-нибудь новое - это всегда работа.', ['что', 'новый', 'это', 'работа']),
  ru('Слово "рука" здесь стоит не случайно.', ['слово', 'рука', 'здесь', 'стоять', 'не']),
  ru('Первый день - самый трудный; дальше легче.', ['первый', 'день', 'самый']),
  ru('Тише едешь - дальше будешь!', ['быть']),
  ru('Он думал так: время всё расставит по местам.', [
    'он',
    'думать',
    'так',
    'время',
    'весь',
    'по',
    'место',
  ]),
  ru('В конце строки (там, у мизинца) ошибок больше.', ['в', 'конец', 'строка', 'ошибка']),
  ru('Мы знаем, что нужно: повторять и не торопиться.', ['мы', 'знать', 'что', 'нужно', 'и', 'не']),
  ru('Какой-то шум мешает; закрой окно, пожалуйста.', ['какой', 'место']),

  ru('Утро началось со стакана воды и короткой разминки.', ['утро', 'вода', 'начать']),
  ru('Дорога до работы занимает почти час.', ['дорога', 'работа', 'час']),
  ru('Он держал ручку слишком крепко и быстро устал.', ['держать', 'ручка', 'быстро', 'уставать']),
  ru('Поставь стул ровно и опусти плечи.', ['стул', 'ровно', 'ставить']),
  ru('Ладони лежат спокойно, пальцы не напряжены.', ['ладонь', 'спокойно', 'палец', 'лежать']),
  ru('Каждый палец отвечает за свой ряд клавиш.', ['палец', 'ряд', 'свой']),
  ru('Хороший ритм важнее короткого рывка.', ['ритм', 'важно', 'хороший']),
  ru('Темп растёт сам, если точность держится.', ['темп', 'расти', 'точность']),
  ru('Небольшая пауза помогает больше, чем упрямство.', ['пауза', 'помогать', 'больше']),
  ru('Повтор нужен не всем словам, а только трудным.', ['повтор', 'нужно', 'слово']),
  ru('Разбор ошибок занимает пять минут в день.', ['разбор', 'ошибка', 'минута', 'день']),
  ru('Мы сравнили два подхода и выбрали простой.', ['сравнивать', 'два', 'подход', 'простой']),
  ru('Она заметила, что стала реже смотреть на клавиши.', ['замечать', 'редко', 'смотреть']),
  ru('Ученик задал вопрос, и учитель ответил сразу.', ['ученик', 'вопрос', 'учитель', 'ответить']),
  ru('Врач посоветовал делать перерыв каждый час.', ['врач', 'час', 'делал']),
  ru('Сестра позвонила поздно вечером.', ['сестра', 'поздно', 'вечер']),
  ru('Брат обещал помочь с переездом в субботу.', ['брат', 'обещать', 'помогать']),
  ru('Сын читает книгу вслух почти каждый вечер.', ['сын', 'читать', 'книга', 'вечер']),
  ru('Дочь нарисовала дом, сад и большое солнце.', ['дочь', 'дом', 'сад', 'солнце']),
  ru('Мама поставила чай на стол.', ['мама', 'чай', 'стол', 'ставить']),
  ru('Папа принёс хлеб и молоко из магазина.', ['папа', 'хлеб', 'молоко', 'магазин']),
  ru('Сосед оставил записку под дверью.', ['сосед', 'дверь', 'под']),
  ru('Гость снял пальто и сел ближе к окну.', ['гость', 'окно', 'ближе']),
  ru('Друзья собрались во дворе после обеда.', ['друг', 'двор', 'обед']),
  ru('Команда закончила проект на неделю раньше.', ['команда', 'проект', 'неделя']),
  ru('Водитель ждал у ворот больше получаса.', ['водитель', 'ждал', 'больше']),
  ru('Мастер починил замок за десять минут.', ['мастер', 'чинить', 'минута']),
  ru('Договор подписали в конце месяца.', ['договор', 'конец', 'месяц']),
  ru('Цена оказалась ниже, чем мы думали.', ['цена', 'ниже', 'думать']),
  ru('Заказ пришёл раньше срока.', ['заказ', 'рано']),
  ru('Товар лежал на верхней полке.', ['товар', 'полка', 'лежать']),
  ru('Выбор оказался проще, чем казалось сначала.', ['выбор', 'просто', 'сначала']),
  ru('Адрес написан на обратной стороне листа.', ['адрес', 'писать', 'сторона']),
  ru('Телефон разрядился в самый неподходящий момент.', ['телефон', 'самый']),
  ru('Программа запускается одной кнопкой.', ['программа', 'кнопка', 'один']),
  ru('Файл сохранился, а папка осталась пустой.', ['файл', 'папка']),
  ru('Сеть работала медленно, но письмо ушло.', ['сеть', 'медленно', 'письмо', 'работать']),
  ru('Страница открылась только с третьего раза.', ['страница', 'открыть']),
  ru('Сайт выглядит проще на маленьком экране.', ['сайт', 'просто', 'экран']),
  ru('Компьютер стоит у окна, слева от стола.', ['компьютер', 'окно', 'стол', 'стоять']),
  ru('Река была тихой, а берег пустым.', ['река', 'берег']),
  ru('Лес начинался сразу за полем.', ['лес', 'поле', 'за']),
  ru('Море шумело всю ночь.', ['море', 'ночь']),
  ru('Гора казалась ближе, чем была на самом деле.', ['гора', 'ближе', 'дело']),
  ru('Небо очистилось к вечеру.', ['небо', 'вечер']),
  ru('Ветер сорвал последние листья.', ['ветер']),
  ru('Дождь шёл всю дорогу до города.', ['дождь', 'дорога', 'город']),
  ru('Снег лежал только на северной стороне двора.', ['снег', 'сторона', 'двор']),
  ru('Лёд на луже треснул под ногой.', ['лёд', 'под']),
  ru('Огонь в печи почти погас.', ['огонь', 'почти']),
  ru('Свет из окна падал на стену.', ['свет', 'окно', 'стена', 'падать']),
  ru('Тень от дерева двигалась вместе с солнцем.', ['тень', 'вместе', 'солнце']),
  ru('Звук был тихим, но узнаваемым.', ['звук']),
  ru('Голос в трубке показался знакомым.', ['голос']),
  ru('Шум с улицы мешал сосредоточиться.', ['шум', 'улица', 'мешать']),
  ru('Запах хлеба слышен от самой двери.', ['запах', 'хлеб', 'дверь']),
  ru('Вкус чая зависит от воды.', ['вкус', 'чай', 'вода']),
  ru('Соль стояла рядом с сахаром.', ['соль', 'рядом', 'стоять']),
  ru('Обед был готов раньше, чем мы вернулись.', ['обед', 'рано']),
  ru('Ужин прошёл тихо и быстро.', ['ужин', 'быстро']),
  ru('Стол оказался слишком высоким для этого стула.', ['стол', 'стул', 'высокий']),
  ru('Дверь закрылась сама.', ['дверь', 'закрыть', 'сам']),
  ru('Окно выходит во двор.', ['окно', 'двор']),
  ru('Стена оказалась тоньше, чем думал сосед.', ['стена', 'сосед', 'думать']),
  ru('Пол под ковром был холодным.', ['пол', 'под', 'холодный']),
  ru('Угол комнаты занял книжный шкаф.', ['угол', 'книга']),
  ru('Полка держится на трёх шурупах.', ['полка', 'держать']),
  ru('Сумка стояла у входа весь день.', ['сумка', 'весь', 'день', 'стоять']),
  ru('В кармане нашлись ключ и монета.', ['карман', 'ключ', 'найти']),
  ru('Бумага закончилась в самый нужный момент.', ['бумага', 'нужно', 'самый']),
  ru('Тетрадь открыта на середине.', ['тетрадь', 'середина', 'открыть']),
  ru('Письмо пришло через неделю.', ['письмо', 'через', 'неделя']),
  ru('Газета лежала на подоконнике второй день.', ['газета', 'лежать', 'день']),
  ru('Журнал оказался старым, но интересным.', ['журнал', 'старый']),
  ru('Рассказ короткий, а запоминается надолго.', ['рассказ', 'запоминать']),
  ru('Песня знакома почти всем.', ['песня', 'почти']),
  ru('Игра закончилась вничью.', ['игра']),
  ru('Спорт приучает к порядку лучше уговоров.', ['спорт', 'порядок', 'лучше']),
  ru('Бег по утрам вошёл в привычку.', ['бег', 'привычка', 'утро']),
  ru('Сон важнее лишнего часа работы.', ['сон', 'важно', 'час', 'работа']),
  ru('Отдых нужен не меньше занятий.', ['отдых', 'нужно', 'меньше', 'занятие']),
  ru('Здоровье дороже любой скорости.', ['здоровье', 'скорость']),
  ru('Семья собирается за столом по воскресеньям.', ['семья', 'стол']),
  ru('Правило простое: сначала точно, потом быстро.', ['правило', 'просто', 'точно', 'быстро']),
  ru('Причина оказалась не в клавиатуре.', ['причина']),
  ru('Пример помогает лучше длинного объяснения.', ['пример', 'помогать', 'лучше']),
  ru('Способ проверить один: набрать незнакомый текст.', ['способ', 'проверять', 'один']),
  ru('Решение приняли не сразу.', ['решение']),
  ru('Значение этого слова я узнал только вчера.', ['значение', 'слово', 'узнавать', 'вчера']),
  ru('Условие простое, а выполнить трудно.', ['условие', 'просто', 'трудно']),
  ru('Качество важнее количества повторов.', ['качество', 'важно', 'повтор']),
  ru('Опыт приходит с числом попыток.', ['опыт', 'число']),
  ru('Движение пальцев стало заметно ровнее.', ['движение', 'палец', 'ровно']),
  ru('Порядок клавиш запоминается руками, а не глазами.', [
    'порядок',
    'запоминать',
    'рука',
    'глаз',
  ]),
  ru('Проверка показала, что скорость выросла.', ['проверка', 'скорость', 'расти']),
  ru('Занятие длится ровно пять минут.', ['занятие', 'ровно', 'минута']),
  ru('Урок закончился раньше звонка.', ['урок']),
  ru('Задача решается в два действия.', ['задача', 'два']),
  ru('Цель на сегодня скромная и достижимая.', ['цель', 'сегодня']),
  ru('План на неделю уместился в одну строку.', ['план', 'неделя', 'строка', 'один']),
  ru('Шаг за шагом получается быстрее, чем рывками.', ['шаг', 'быстро']),
];
